import { useState } from 'react';
import { AlertTriangle, ArrowDown, ArrowUp, CheckCircle2, Loader2, Plus, RefreshCcw, Star, Trash2 } from 'lucide-react';
import { ApiService } from '../../services/api';
import type { InstallmentFeeBearer, InstallmentPolicyResult, PaymentPackage, PaymentPackageUnit } from '../../types/payment';
import { formatMoney } from '../payment/paymentFormat';

// Ödeme ayarlarındaki paket düzenleyici: paket adı, süresi, fiyatı, taksit politikası, açtığı bölümler
// ve ödeme sayfasındaki görünümü buradan yönetilir (koda gömülü değildir). Paket KİMLİĞİ sabittir;
// ad değişse de geçmiş siparişler bozulmaz.

// Düzenleme sırasında fiyat/süre metin olarak tutulur (yarım yazılmış "27." gibi değerler bozulmasın).
export type EditablePackage = Omit<PaymentPackage, 'id' | 'price' | 'value' | 'features' | 'entryCount' | 'referralDiscount'> & {
  id?: string;
  _key: string;
  price: string | number;
  value: string | number;
  entryCount?: string | number | null;
  referralDiscount?: string | number;
  featuresText: string; // kartta yazacak satırlar, satır başına bir madde
};

type ModuleOption = { id: string; label: string };

type Props = {
  packages: EditablePackage[];
  installments: number[];
  packageUnits: Record<string, number>;
  modules: ModuleOption[];
  paidModuleIds: string[];
  onChange: (packages: EditablePackage[]) => void;
};

// Süre seçimi: gün/ay sayısı, herkes için aynı bitiş günü ya da giriş kartı.
type DurationChoice = PaymentPackageUnit | 'fixed' | 'credits';
const DURATION_LABELS: Record<DurationChoice, string> = { day: 'Gün', month: 'Ay', fixed: 'Tarihe kadar', credits: 'Giriş hakkı' };
const QR_MODULE_ID = 'akademi-qr';

const INPUT_CLASS =
  'w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800 outline-none transition focus:border-violet-300 focus:bg-white focus:ring-4 focus:ring-violet-50 disabled:cursor-not-allowed disabled:opacity-50';
const FIELD_LABEL_CLASS = 'mb-1 block text-xs font-semibold text-slate-500';
// Yan yana duran sayı + birim kutuları: w-full olmadan (yoksa birim kutusu sayıyı ezer).
const INLINE_INPUT_CLASS = INPUT_CLASS.replace('w-full ', '');

let newKeyCounter = 0;

export function toEditablePackages(packages: PaymentPackage[] = []): EditablePackage[] {
  return packages.map(({ features, ...item }) => ({ ...item, _key: item.id, featuresText: (features || []).join('\n') }));
}

// Kaydetmeden önce sunucunun beklediği sade biçime çevirir (yeni pakette id yoktur, sunucu üretir).
export function toPackagePayload(packages: EditablePackage[]) {
  return packages.map((item) => {
    const fixed = item.durationType === 'fixed';
    const credits = item.durationType === 'credits';
    const moduleEndsOn = fixed
      ? Object.fromEntries(Object.entries(item.moduleEndsOn || {}).filter(([, ymd]) => ymd))
      : {};
    return {
      id: item.id,
      label: String(item.label || '').trim(),
      unit: credits ? 'day' : item.unit,
      value: Number(item.value),
      durationType: item.durationType || 'relative',
      ...(fixed ? { endsOn: item.endsOn || '' } : {}),
      ...(credits ? { entryCount: Number(item.entryCount) } : {}),
      ...(Object.keys(moduleEndsOn).length ? { moduleEndsOn } : {}),
      moduleIds: Array.isArray(item.moduleIds) ? item.moduleIds : null,
      qrExamDaysOnly: !credits && Boolean(item.qrExamDaysOnly),
      group: String(item.group || '').trim(),
      recommended: Boolean(item.recommended),
      features: String(item.featuresText || '').split('\n').map((line) => line.trim()).filter(Boolean),
      referralDiscount: Number(String(item.referralDiscount ?? '').replace(',', '.')) || 0,
      referrerReward: String(item.referrerReward || '').trim(),
      bridgeCredit: Boolean(item.bridgeCredit),
      price: Number(String(item.price).replace(',', '.')) || 0,
      maxInstallment: Number(item.maxInstallment) || 1,
      installmentFeeBearer: item.installmentFeeBearer,
      enabled: item.enabled !== false,
    };
  });
}

// Ödeme ekranında müşterinin göreceği taksit ifadesi (PaymentPage ile aynı dil).
function installmentSummary(item: EditablePackage): string {
  const max = Number(item.maxInstallment) || 1;
  const price = Number(String(item.price).replace(',', '.')) || 0;
  if (max <= 1) return 'Tek çekim';
  if (item.installmentFeeBearer === 'merchant') {
    return `Peşin fiyatına ${max} taksit${price > 0 ? ` (${max} × ${formatMoney(price / max)})` : ''}`;
  }
  return `${max} taksite kadar, vade farkı müşteriden`;
}

function formatDay(ymd?: string | null): string {
  if (!ymd) return '';
  const date = new Date(`${ymd}T12:00:00`);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' });
}

function durationSummary(item: EditablePackage): string {
  if (item.durationType === 'fixed') {
    return item.endsOn ? `Herkes için ${formatDay(item.endsOn)} günü sonunda biter.` : 'Bitiş tarihini seçin.';
  }
  if (item.durationType === 'credits') {
    return `Giriş kartı: ${Number(item.entryCount) || 0} giriş, alındığı günden ${Number(item.value) || 0} gün geçerli. Kapı kodu alınan her gün bir giriş düşer; kalanlar süre sonunda yanar.`;
  }
  if (item.unit === 'day' && Number(item.value) === 1) return 'Günlük paket: ertesi gün 07:30’da (QR açılışında) biter.';
  return 'Alındığı günden itibaren sayılır; aynı türden paketi olanın süresi üstüne eklenir.';
}

export default function PaymentPackagesEditor({ packages, installments, packageUnits, modules, paidModuleIds, onChange }: Props) {
  const [policy, setPolicy] = useState<InstallmentPolicyResult | null>(null);
  const [policyLoading, setPolicyLoading] = useState(false);

  const moduleLabel = new Map(modules.map((module) => [module.id, module.label]));
  const groups = [...new Set(packages.map((item) => String(item.group || '').trim()).filter(Boolean))];

  const update = (key: string, patch: Partial<EditablePackage>) =>
    onChange(packages.map((item) => (item._key === key ? { ...item, ...patch } : item)));

  // Önerilen paket tektir: ödeme sayfası onu seçili açar.
  const setRecommended = (key: string, recommended: boolean) =>
    onChange(packages.map((item) => ({ ...item, recommended: item._key === key ? recommended : recommended ? false : item.recommended })));

  const setDuration = (item: EditablePackage, choice: DurationChoice) => {
    if (choice === 'fixed') update(item._key, { durationType: 'fixed' });
    else if (choice === 'credits') {
      update(item._key, {
        durationType: 'credits',
        unit: 'day',
        entryCount: Number(item.entryCount) || 10,
        value: item.unit === 'day' && Number(item.value) > 1 ? item.value : 30,
        qrExamDaysOnly: false,
        moduleIds: [QR_MODULE_ID],
      });
    } else update(item._key, { durationType: 'relative', unit: choice });
  };

  // null = tüm ücretli bölümler. İlk dokunuşta açık listeye çevrilir.
  const toggleModule = (item: EditablePackage, moduleId: string) => {
    const current = Array.isArray(item.moduleIds) ? item.moduleIds : paidModuleIds;
    const next = current.includes(moduleId) ? current.filter((id) => id !== moduleId) : [...current, moduleId];
    update(item._key, { moduleIds: next });
  };

  const move = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= packages.length) return;
    const next = [...packages];
    [next[index], next[target]] = [next[target], next[index]];
    onChange(next);
  };

  const add = () => {
    newKeyCounter += 1;
    onChange([
      ...packages,
      {
        _key: `new-${Date.now()}-${newKeyCounter}`,
        label: '',
        unit: 'month',
        value: 1,
        durationType: 'relative',
        endsOn: null,
        moduleEndsOn: null,
        moduleIds: [...paidModuleIds],
        group: '',
        recommended: false,
        featuresText: '',
        price: '',
        currency: 'TRY',
        enabled: true,
        active: false,
        maxInstallment: 1,
        installmentFeeBearer: 'merchant',
        enabledInstallments: [1],
      },
    ]);
  };

  const checkPolicy = async () => {
    setPolicyLoading(true);
    try {
      setPolicy(await ApiService.getPaymentInstallmentPolicy());
    } catch (error: any) {
      setPolicy({ available: false, reason: error?.message || 'Taksit bilgisi alınamadı.', packages: [] });
    } finally {
      setPolicyLoading(false);
    }
  };

  return (
    <div>
      <div className='flex flex-wrap items-start justify-between gap-3'>
        <div>
          <h2 className='font-semibold text-slate-900'>Paketler</h2>
          <p className='mt-1 text-sm text-slate-500'>
            Paket adını, süresini, fiyatını, taksit seçeneğini ve hangi bölümleri açtığını buradan belirleyin. Ödeme ekranında bu sırayla,
            “Sayfadaki başlık” altında gruplanarak görünür. Adı istediğiniz zaman değiştirebilirsiniz; geçmiş ödemeler satın alındığı günkü
            haliyle kalır.
          </p>
        </div>
        <button
          type='button'
          onClick={add}
          className='inline-flex items-center gap-1.5 rounded-xl border border-violet-200 bg-violet-50 px-3 py-2 text-sm font-semibold text-violet-700 transition hover:bg-violet-100'
        >
          <Plus className='h-4 w-4' />Paket Ekle
        </button>
      </div>

      <datalist id='payment-package-groups'>
        {groups.map((group) => <option key={group} value={group} />)}
      </datalist>

      <div className='mt-4 space-y-3'>
        {packages.length === 0 && (
          <p className='rounded-xl border border-dashed border-slate-300 p-4 text-center text-sm text-slate-500'>
            Henüz paket yok. “Paket Ekle” ile başlayın.
          </p>
        )}
        {packages.map((item, index) => {
          const singlePayment = (Number(item.maxInstallment) || 1) <= 1;
          const onSale = item.enabled !== false;
          const fixed = item.durationType === 'fixed';
          const credits = item.durationType === 'credits';
          const durationChoice: DurationChoice = fixed ? 'fixed' : credits ? 'credits' : item.unit;
          const allModules = !Array.isArray(item.moduleIds);
          const selectedModules = allModules ? paidModuleIds : (item.moduleIds as string[]);
          // Ücretli listeden çıkarılmış ama pakette kalan bölüm de görünsün ki çıkarılabilsin.
          const moduleChoices = modules.filter((module) => paidModuleIds.includes(module.id) || selectedModules.includes(module.id));
          return (
            <div
              key={item._key}
              className={`rounded-xl border p-3.5 transition ${onSale ? 'border-slate-200 bg-white' : 'border-slate-200 bg-slate-50 opacity-75'}`}
            >
              <div className='grid gap-3 sm:grid-cols-2 lg:grid-cols-[minmax(0,1.6fr)_minmax(0,1.3fr)_minmax(0,1fr)_minmax(0,1.1fr)_minmax(0,1.5fr)]'>
                <label className='block'>
                  <span className={FIELD_LABEL_CLASS}>Paket adı</span>
                  <input
                    type='text'
                    value={item.label}
                    maxLength={60}
                    placeholder='Örn. Eğitim Sezonu'
                    onChange={(event) => update(item._key, { label: event.target.value })}
                    className={INPUT_CLASS}
                  />
                </label>

                <div>
                  <span className={FIELD_LABEL_CLASS}>Süre</span>
                  {fixed || credits ? (
                    // Tarih ve giriş kartı alanları dar sütuna sığmaz: seçim üstte, değer kendi satırında.
                    <div className='flex flex-col gap-1.5'>
                      <select
                        value={durationChoice}
                        onChange={(event) => setDuration(item, event.target.value as DurationChoice)}
                        className={INPUT_CLASS}
                      >
                        {(Object.keys(DURATION_LABELS) as DurationChoice[]).map((choice) => (
                          <option key={choice} value={choice}>{DURATION_LABELS[choice]}</option>
                        ))}
                      </select>
                      {fixed ? (
                        <input
                          type='date'
                          aria-label='Bitiş tarihi'
                          value={item.endsOn || ''}
                          onChange={(event) => update(item._key, { endsOn: event.target.value })}
                          className={INPUT_CLASS}
                        />
                      ) : (
                        <div className='flex items-center gap-1.5 text-xs text-slate-500'>
                          <input
                            type='number'
                            min={1}
                            max={100}
                            aria-label='Giriş sayısı'
                            value={item.entryCount ?? ''}
                            onChange={(event) => update(item._key, { entryCount: event.target.value })}
                            className={`${INLINE_INPUT_CLASS} min-w-0 flex-1`}
                          />
                          giriş
                          <input
                            type='number'
                            min={1}
                            max={365}
                            aria-label='Kaç gün geçerli'
                            value={item.value}
                            onChange={(event) => update(item._key, { value: event.target.value })}
                            className={`${INLINE_INPUT_CLASS} min-w-0 flex-1`}
                          />
                          gün
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className='flex gap-1.5'>
                      <input
                        type='number'
                        min={1}
                        max={packageUnits[item.unit] || undefined}
                        value={item.value}
                        onChange={(event) => update(item._key, { value: event.target.value })}
                        className={`${INLINE_INPUT_CLASS} min-w-0 flex-1`}
                      />
                      <select
                        value={durationChoice}
                        onChange={(event) => setDuration(item, event.target.value as DurationChoice)}
                        className={`${INLINE_INPUT_CLASS} w-28 flex-none`}
                      >
                        {(Object.keys(DURATION_LABELS) as DurationChoice[]).map((choice) => (
                          <option key={choice} value={choice}>{DURATION_LABELS[choice]}</option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>

                <label className='block'>
                  <span className={FIELD_LABEL_CLASS}>Fiyat (TRY)</span>
                  <input
                    type='text'
                    inputMode='decimal'
                    value={item.price}
                    placeholder='0'
                    onChange={(event) => update(item._key, { price: event.target.value.replace(',', '.').replace(/[^0-9.]/g, '') })}
                    className={INPUT_CLASS}
                  />
                </label>

                <label className='block'>
                  <span className={FIELD_LABEL_CLASS}>Taksit</span>
                  <select
                    value={Number(item.maxInstallment) || 1}
                    onChange={(event) => update(item._key, { maxInstallment: Number(event.target.value) })}
                    className={INPUT_CLASS}
                  >
                    {installments.map((count) => (
                      <option key={count} value={count}>{count === 1 ? 'Tek çekim' : `${count} taksite kadar`}</option>
                    ))}
                  </select>
                </label>

                <label className='block'>
                  <span className={FIELD_LABEL_CLASS}>Taksit vade farkı</span>
                  <select
                    value={item.installmentFeeBearer}
                    disabled={singlePayment}
                    onChange={(event) => update(item._key, { installmentFeeBearer: event.target.value as InstallmentFeeBearer })}
                    className={INPUT_CLASS}
                  >
                    <option value='merchant'>Bana yansısın (peşin fiyatına)</option>
                    <option value='customer'>Müşteriye yansısın</option>
                  </select>
                </label>
              </div>

              <div className='mt-3'>
                <span className={FIELD_LABEL_CLASS}>
                  Açtığı bölümler
                  {allModules && <span className='ml-1 font-normal text-slate-400'>(seçilmemiş: tüm ücretli bölümler)</span>}
                </span>
                <div className='flex flex-wrap gap-1.5'>
                  {moduleChoices.length === 0 && (
                    <span className='text-xs text-slate-500'>Önce yukarıdan ücretli modül seçin.</span>
                  )}
                  {moduleChoices.map((module) => {
                    const on = selectedModules.includes(module.id);
                    const notPaid = !paidModuleIds.includes(module.id);
                    return (
                      <button
                        key={module.id}
                        type='button'
                        aria-pressed={on}
                        onClick={() => toggleModule(item, module.id)}
                        title={notPaid ? 'Bu bölüm ücretli değil; kaydetmeden önce paketten çıkarın.' : undefined}
                        className={`inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1 text-xs font-semibold transition ${
                          notPaid
                            ? 'border-red-200 bg-red-50 text-red-700'
                            : on
                              ? 'border-violet-300 bg-violet-50 text-violet-800'
                              : 'border-slate-200 bg-white text-slate-500 hover:border-slate-300'
                        }`}
                      >
                        <span className={`h-3 w-3 rounded-sm border ${on ? 'border-violet-600 bg-violet-600' : 'border-slate-300 bg-white'}`} />
                        {module.label}
                        {notPaid && ' · ücretli değil'}
                      </button>
                    );
                  })}
                </div>
                {!credits && selectedModules.includes(QR_MODULE_ID) && paidModuleIds.includes(QR_MODULE_ID) && (
                  <label className='mt-2 inline-flex cursor-pointer items-center gap-1.5 text-xs font-semibold text-slate-600'>
                    <input
                      type='checkbox'
                      checked={Boolean(item.qrExamDaysOnly)}
                      onChange={(event) => update(item._key, { qrExamDaysOnly: event.target.checked })}
                      className='h-4 w-4 accent-violet-600'
                    />
                    Salon kapısı yalnız deneme günlerinde açık (Deneme takvimi aşağıda)
                  </label>
                )}
                {fixed && selectedModules.length > 1 && (
                  <div className='mt-2.5 rounded-lg bg-slate-50 p-2.5'>
                    <p className='text-xs font-semibold text-slate-500'>Daha erken kapanan bölüm (isteğe bağlı)</p>
                    <div className='mt-1.5 grid gap-2 sm:grid-cols-2 lg:grid-cols-3'>
                      {selectedModules.map((moduleId) => (
                        <label key={moduleId} className='flex items-center gap-2 text-xs text-slate-600'>
                          <span className='min-w-0 flex-1 truncate'>{moduleLabel.get(moduleId) || moduleId}</span>
                          <input
                            type='date'
                            value={item.moduleEndsOn?.[moduleId] || ''}
                            max={item.endsOn || undefined}
                            onChange={(event) => update(item._key, { moduleEndsOn: { ...(item.moduleEndsOn || {}), [moduleId]: event.target.value } })}
                            className='w-36 rounded-md border border-slate-200 bg-white px-2 py-1 text-xs'
                          />
                        </label>
                      ))}
                    </div>
                    <p className='mt-1.5 text-[11px] text-slate-500'>Boş bırakılan bölüm paketin bitişine kadar açık kalır. Örn. salon YKS gününde, dijital bölümler 20 Temmuz’da kapanır.</p>
                  </div>
                )}
              </div>

              <div className='mt-3 grid gap-3 sm:grid-cols-[minmax(0,1fr)_minmax(0,2fr)]'>
                <label className='block'>
                  <span className={FIELD_LABEL_CLASS}>Sayfadaki başlık</span>
                  <input
                    type='text'
                    list='payment-package-groups'
                    value={item.group || ''}
                    maxLength={40}
                    placeholder='Örn. Çalışma Alanı'
                    onChange={(event) => update(item._key, { group: event.target.value })}
                    className={INPUT_CLASS}
                  />
                </label>
                <label className='block'>
                  <span className={FIELD_LABEL_CLASS}>Kartta yazacaklar (her satır bir madde, en fazla 8)</span>
                  <textarea
                    rows={2}
                    value={item.featuresText}
                    placeholder={'Örn. Her gün 08:00–24:00\nBir gün içinde sınırsız giriş-çıkış'}
                    onChange={(event) => update(item._key, { featuresText: event.target.value })}
                    className={`${INPUT_CLASS} resize-y`}
                  />
                </label>
              </div>

              <div className='mt-3 grid gap-3 rounded-lg bg-amber-50/60 p-2.5 sm:grid-cols-[minmax(0,1fr)_minmax(0,2fr)]'>
                <label className='block'>
                  <span className={FIELD_LABEL_CLASS}>Davetle alana indirim (TRY)</span>
                  <input
                    type='text'
                    inputMode='decimal'
                    value={item.referralDiscount ?? ''}
                    placeholder='0 = davet indirimi yok'
                    onChange={(event) => update(item._key, { referralDiscount: event.target.value.replace(',', '.').replace(/[^0-9.]/g, '') })}
                    className={INPUT_CLASS}
                  />
                </label>
                <label className='block'>
                  <span className={FIELD_LABEL_CLASS}>Getirene ödül (ödül listesinde ve davet mesajında yazar)</span>
                  <input
                    type='text'
                    value={item.referrerReward || ''}
                    maxLength={80}
                    placeholder='Örn. 5.000 ₺ Boyner veya D&R hediye çeki'
                    onChange={(event) => update(item._key, { referrerReward: event.target.value })}
                    className={INPUT_CLASS}
                  />
                </label>
                <label className='inline-flex cursor-pointer items-center gap-1.5 text-xs font-semibold text-slate-600 sm:col-span-2'>
                  <input
                    type='checkbox'
                    checked={Boolean(item.bridgeCredit)}
                    onChange={(event) => update(item._key, { bridgeCredit: event.target.checked })}
                    className='h-4 w-4 accent-violet-600'
                  />
                  Son 30 günde ödenen diğer paketleri (salon, deneme) bu paketin fiyatından düş
                </label>
              </div>

              <div className='mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-slate-100 pt-2.5'>
                <p className='text-xs text-slate-500'>
                  {durationSummary(item)}{' '}
                  Müşteri görür: <span className='font-semibold text-slate-700'>{installmentSummary(item)}</span>
                </p>
                <div className='flex items-center gap-1'>
                  <label className='mr-2 inline-flex cursor-pointer items-center gap-1.5 text-xs font-semibold text-slate-600'>
                    <input
                      type='checkbox'
                      checked={Boolean(item.recommended)}
                      onChange={(event) => setRecommended(item._key, event.target.checked)}
                      className='h-4 w-4 accent-violet-600'
                    />
                    <Star className='h-3.5 w-3.5 text-violet-500' />Önerilen
                  </label>
                  <label className='mr-2 inline-flex cursor-pointer items-center gap-1.5 text-xs font-semibold text-slate-600'>
                    <input
                      type='checkbox'
                      checked={onSale}
                      onChange={(event) => update(item._key, { enabled: event.target.checked })}
                      className='h-4 w-4 accent-violet-600'
                    />
                    Satışta
                  </label>
                  <button type='button' title='Yukarı taşı' disabled={index === 0} onClick={() => move(index, -1)} className='rounded-lg p-1.5 text-slate-500 hover:bg-slate-100 disabled:opacity-30'>
                    <ArrowUp className='h-4 w-4' />
                  </button>
                  <button type='button' title='Aşağı taşı' disabled={index === packages.length - 1} onClick={() => move(index, 1)} className='rounded-lg p-1.5 text-slate-500 hover:bg-slate-100 disabled:opacity-30'>
                    <ArrowDown className='h-4 w-4' />
                  </button>
                  <button type='button' title='Paketi sil' onClick={() => onChange(packages.filter((other) => other._key !== item._key))} className='rounded-lg p-1.5 text-red-500 hover:bg-red-50'>
                    <Trash2 className='h-4 w-4' />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* iyzico panel denetimi: vade farkını gerçekte kimin ödediği orada belirlenir */}
      <div className='mt-4 rounded-xl border border-amber-200 bg-amber-50 p-3.5'>
        <div className='flex flex-wrap items-start justify-between gap-3'>
          <p className='min-w-0 flex-1 text-xs leading-relaxed text-amber-900'>
            <strong>Önemli:</strong> Vade farkını kimin ödediğini iyzico belirler. “Bana yansısın” seçtiğiniz taksit sayıları için iyzico
            panelinde <strong>Taksit ve Komisyon Yönetimi</strong> sayfasından vade farkını sizin üstlendiğinizi de işaretlemelisiniz; bu ayar
            hesap genelidir (aynı taksit sayısı bir pakette size, başka pakette müşteriye yansıyamaz). Kaydettikten sonra aşağıdan denetleyin.
          </p>
          <button
            type='button'
            onClick={checkPolicy}
            disabled={policyLoading}
            className='inline-flex flex-none items-center gap-1.5 rounded-lg border border-amber-300 bg-white px-3 py-1.5 text-xs font-semibold text-amber-900 transition hover:bg-amber-100 disabled:opacity-60'
          >
            {policyLoading ? <Loader2 className='h-3.5 w-3.5 animate-spin' /> : <RefreshCcw className='h-3.5 w-3.5' />}
            iyzico ayarını denetle
          </button>
        </div>

        {policy && !policy.available && (
          <p className='mt-3 text-xs font-medium text-amber-900'>{policy.reason}</p>
        )}
        {policy?.available && policy.packages.length === 0 && (
          <p className='mt-3 text-xs font-medium text-amber-900'>Kayıtlı paketler arasında taksitli ve satışta olan paket yok (değişiklik yaptıysanız önce kaydedin).</p>
        )}
        {policy?.available && policy.packages.map((pkg) => (
          <div key={pkg.id} className='mt-3 rounded-lg bg-white p-3'>
            <div className='text-xs font-bold text-slate-800'>
              {pkg.label} · {formatMoney(pkg.price)} ·{' '}
              {pkg.installmentFeeBearer === 'merchant' ? 'beyan: peşin fiyatına taksit' : 'beyan: vade farkı müşteriden'}
            </div>
            <ul className='mt-1.5 space-y-1'>
              {pkg.rows.map((row) => (
                <li key={row.count} className={`flex items-start gap-1.5 text-xs ${row.matches ? 'text-emerald-700' : 'text-red-700'}`}>
                  {row.matches ? <CheckCircle2 className='mt-0.5 h-3.5 w-3.5 flex-none' /> : <AlertTriangle className='mt-0.5 h-3.5 w-3.5 flex-none' />}
                  <span>
                    <strong>{row.count} taksit:</strong>{' '}
                    {row.bearer === 'unavailable' && 'iyzico bu tutarda bu taksiti sunmuyor.'}
                    {row.bearer === 'merchant' && `müşteri ${formatMoney(row.maxTotal)} öder — vade farkı sizde.`}
                    {row.bearer === 'customer' && `müşteri ${formatMoney(row.minTotal)}${row.maxTotal !== row.minTotal ? ` – ${formatMoney(row.maxTotal)}` : ''} öder — vade farkı müşteride.`}
                    {row.bearer === 'mixed' && `karta göre değişiyor (${formatMoney(row.minTotal)} – ${formatMoney(row.maxTotal)}).`}
                    {!row.matches && row.bearer !== 'unavailable' && ' Paketteki seçimle uyuşmuyor; iyzico panelini ya da paketi düzeltin.'}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}
